import { action, computed, observable } from "mobx";
import debug from "debug";
import { isEqual } from "lodash";
import { FormStore } from "./FormStore";
import { FieldState, FieldOptions } from "./types";

const noTransform = (v) => v;

export class Field<T> {
  private _log: debug.IDebugger;

  @observable private _disabled = false;
  @observable private _focused = false;
  @observable private _touched = false;

  constructor(
    private readonly _store: FormStore<any>,
    private readonly _name: string,
    private readonly _options: FieldOptions<any> = {}
  ) {
    this._log = debug(`app:Field:${this._name}`);
    this._options.parseValue = this._options.parseValue || noTransform;
    this._options.transformValue = this._options.transformValue || noTransform;
  }

  @computed public get dirty() {
    return !isEqual(
      this._store.getValue(this._name),
      this._store.getInitialValue(this._name)
    );
  }

  @computed public get disabled() {
    return this._store.disabled || this._disabled;
  }

  public set disabled(v: boolean) {
    this._disabled = v;
  }

  @computed public get focused() {
    return this._focused;
  }

  public set focused(v: boolean) {
    this._log("set focused", v);
    this._focused = v;
  }

  @computed public get name(): string {
    return this._name;
  }

  @computed public get initialValue(): T {
    return this._store.getInitialValue(this._name);
  }

  @computed public get state(): FieldState<T> {
    return {
      clear: !this.dirty,
      dirty: this.dirty,
      disabled: this.disabled,
      error: this.error,
      focused: this.focused,
      initialValue: this.initialValue,
      invalid: !this.valid,
      name: this.name,
      touched: this.touched,
      valid: this.valid,
      value: this.value
    };
  }

  @computed public get touched() {
    return this._touched;
  }

  public set touched(v: boolean) {
    this._log("set touched", v);
    this._touched = v;
  }

  @computed public get invalid() {
    return !this.valid;
  }

  @computed public get valid() {
    return !this.error;
  }

  @computed public get error() {
    return this._store.getError(this._name);
  }

  @computed public get value() {
    const { parseValue } = this._options;

    return parseValue(this._store.getValue(this._name) as T);
  }

  public set value(v: T) {
    const { transformValue } = this._options;

    this._store.setValue(this._name, transformValue(v));
  }

  @computed public get fieldProps() {
    return {
      disabled: this.disabled,
      onBlur: this.onBlur,
      onChange: this.onChangeHandler,
      onFocus: this.onFocusHandler,
      name: this.name,
      value: this.value
    };
  }

  @action.bound protected onBlur() {
    this._log("onBlur");
    this._focused = false;
    this.touched = true;
  }

  @action.bound protected onChangeHandler(e) {
    this._log("onChange", e);

    if (e && e.target) {
      this.value = e.target.value;
      return;
    }

    this.value = e;
  }

  @action.bound protected onFocusHandler() {
    this._log("onFocus");
    this._focused = true;
  }
}
